/**
 * Онбординг компании — чистая логика без React (AK-572).
 *
 * Здесь живёт всё, что можно проверить тестом без рендера: перевод названия
 * в slug, проверка полей формы, подписи статусов приглашений. Компоненты
 * рядом занимаются только разметкой.
 */

import { roleLabel } from '@/pages/team/teamLogic';

/** Кириллица → латиница. Тот же набор, что в `pages/company/roles/rolesLogic`. */
const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

/** Бэкенд: `^[a-z][a-z0-9_]{1,30}$` (`platform.py::SLUG_RE`). */
export const SLUG_MAX_LENGTH = 31;
const SLUG_RE = /^[a-z][a-z0-9_]{1,30}$/;

/**
 * «Агрохолдинг Просторы» → `agroholding_prostory`.
 *
 * Случайного суффикса тут нет намеренно (в отличие от кода роли): slug —
 * это адрес схемы тенанта, его читают люди в psql и в логах, и `prostory_x7f2`
 * там хуже, чем понятный `prostory`. Занятость slug'а проверяет сервер (409),
 * а человек в диалоге может поправить строку руками.
 */
export function slugFromName(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .split('')
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_');
  // Первый символ обязан быть буквой: название вида «7 ветров» дало бы
  // `7_vetrov`, который сервер отбил бы 422 уже после заполнения всей формы.
  const withLetter = /^[a-z]/.test(base) ? base : base ? `c_${base}` : '';
  return withLetter.slice(0, SLUG_MAX_LENGTH).replace(/_+$/, '');
}

/** `null` — годный slug; иначе текст ошибки для поля. */
export function validateSlug(slug: string): string | null {
  if (!slug.trim()) return 'Укажите адрес компании';
  if (slug.length < 2) return 'Минимум 2 символа';
  if (slug.length > SLUG_MAX_LENGTH) return `Не длиннее ${SLUG_MAX_LENGTH} символов`;
  if (!SLUG_RE.test(slug)) {
    return 'Только латиница, цифры и подчёркивание; начинается с буквы';
  }
  return null;
}

/** Грубая проверка почты — финальную делает сервер (`EmailStr`). */
export function validateEmail(email: string): string | null {
  const v = email.trim();
  if (!v) return 'Укажите почту';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Похоже, в почте опечатка';
  return null;
}

export interface CreateCompanyDraft {
  name: string;
  slug: string;
  emailDomain: string;
  /** Звать администратора сейчас или отложить приглашение на потом. */
  inviteAdmin: boolean;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
  personalMessage: string;
  skipEmail: boolean;
}

export const EMPTY_CREATE_COMPANY_DRAFT: CreateCompanyDraft = {
  name: '',
  slug: '',
  emailDomain: '',
  // По умолчанию зовём сразу: это самый частый сценарий, и молчаливый
  // «приглашу позже» оставлял бы компанию без единого человека незаметно.
  inviteAdmin: true,
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
  personalMessage: '',
  skipEmail: false,
};

export interface CreateCompanyErrors {
  name?: string;
  slug?: string;
  adminEmail?: string;
}

export function validateCreateCompany(
  draft: CreateCompanyDraft,
): CreateCompanyErrors {
  const errors: CreateCompanyErrors = {};
  if (!draft.name.trim()) errors.name = 'Укажите название компании';
  const slugError = validateSlug(draft.slug);
  if (slugError) errors.slug = slugError;
  // Почту проверяем только когда админа зовут сейчас. При «позже» пустое поле
  // — не промах, а выбранный сценарий, и требовать адрес значило бы запереть
  // форму ради приглашения, которого не будет.
  if (draft.inviteAdmin) {
    const emailError = validateEmail(draft.adminEmail);
    if (emailError) errors.adminEmail = emailError;
  }
  return errors;
}

export function hasErrors(errors: CreateCompanyErrors): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Роли, которые платформа выдаёт при заведении компании. Зеркало
 * `api/v1/platform.py::ALLOWED_INVITE_ROLES` = `INVITABLE_ROLES` без ролей
 * хозяйства: `farm_admin` / `farm_chief_agronomist` выдают админ компании или
 * админ хозяйства в «Команде», с проверкой хозяйства (ADR-081 §4).
 * Метки — общая карта `roleLabel` (одна на весь фронт).
 */
export const INVITE_ROLE_OPTIONS: { value: string; label: string }[] = [
  'company_admin',
  'top_level',
  'board',
  'clevel',
  'clevel_agronom',
  'agronom',
  'monitoring_lady',
  'viewer',
].map((value) => ({ value, label: roleLabel(value) }));

export function inviteRoleLabel(role: string | null | undefined): string {
  if (!role) return '—';
  return INVITE_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role;
}

export type InvitationStatus = 'accepted' | 'revoked' | 'expired' | 'pending';

/** Подпись статуса — глазами человека, а не таблицы: «вошёл», а не «accepted». */
export function invitationStatusLabel(status: InvitationStatus): string {
  switch (status) {
    case 'accepted':
      return 'Вошёл';
    case 'revoked':
      return 'Отозвано';
    case 'expired':
      return 'Просрочено';
    default:
      return 'Ждёт входа';
  }
}

export function invitationStatusVariant(
  status: InvitationStatus,
): 'primary' | 'secondary' | 'destructive' | 'accent-brand' {
  switch (status) {
    case 'accepted':
      return 'primary';
    case 'revoked':
      return 'destructive';
    case 'expired':
      return 'accent-brand';
    default:
      return 'secondary';
  }
}

const dateTimeFmt = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function formatInviteDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '—';
  return dateTimeFmt.format(new Date(ts));
}

/**
 * ФИО так, как его знает бэкенд; если имени нет — почта (никогда не пустая
 * строка). Один helper на все списки людей платформы: `Users`, `Admins` и
 * участники компании показывают человека одинаково, иначе один и тот же
 * человек выглядел бы в трёх местах по-разному.
 */
export function personDisplayName(p: {
  email: string;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}): string {
  const display = (p.display_name ?? '').trim();
  if (display) return display;
  const composed = [p.last_name, p.first_name]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return composed || p.email;
}
